import React, { useState } from 'react';
import clsx from 'clsx';
import { KEYBOARD_EVENTS } from '../../utils/constants/commonConstants';
import styles from './ProfileDropdownSheet.module.scss';

export type ProfileOption = {
    label: string;
    img?: string | JSX.Element;
    hoverImg?: string | JSX.Element;
    show: boolean;
    action?: () => void;
    subOptions?: Array<{
        label: string;
        show: boolean;
        action: () => void;
    }>;
};

type ProfileDropdownSheetProps = {
    options: ProfileOption[];
    onClose: () => void;
};

const renderOptionIcon = (option: ProfileOption, isHovered: boolean) => {
    const iconToRender = isHovered ? option.hoverImg : option.img;
    if (!iconToRender) return null;

    if (typeof iconToRender === 'string') {
        return <img src={iconToRender} alt="" aria-hidden="true" height={15} width={15} className={styles.OptionImage} />;
    }

    return iconToRender;
};

// A sub-options row renders its sub-labels only, so one with nothing left to show has no
// label of its own to fall back on.
const isRowVisible = (option: ProfileOption) =>
    option.show && (!option.subOptions || option.subOptions.some((subOption) => subOption.show));

// Every selectable row is a div, so the role, focusability and Enter/Space path a menu item
// needs have to be added by hand (same shape as AskAicaQuestionBadge's role="button" fix).
const menuItemProps = (select: () => void) => ({
    role: 'menuitem',
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
            e.preventDefault();
            select();
        }
    },
});

/**
 * The profile dropdown panel — an icon+label row per option, with an optional row of
 * inline sub-options instead of a label (the Terms of use / Privacy policy footer).
 * Presentation only: the caller owns which options exist and when they show.
 */
function ProfileDropdownSheet({ options, onClose }: ProfileDropdownSheetProps) {
    const [hoveredOptionLabel, setHoveredOptionLabel] = useState<string | null>(null);

    return (
        <div className={styles.Options} role="menu" aria-label="Profile options">
            {options.filter(isRowVisible).map((option) => {
                const visibleSubOptions = option.subOptions?.filter((subOption) => subOption.show) ?? [];
                const hasSubOptions = visibleSubOptions.length > 0;
                const selectOption = () => {
                    onClose();
                    option.action?.();
                };

                return (
                    <div
                        // A sub-options row holds its own menu items, so it groups them rather
                        // than being one itself — a menuitem may not contain a menuitem.
                        {...(hasSubOptions ? { role: 'group' } : menuItemProps(selectOption))}
                        className={clsx(styles.Option, { [styles.HasSubOptions]: hasSubOptions })}
                        key={option.label}
                        onMouseEnter={() => setHoveredOptionLabel(option.label)}
                        onMouseLeave={() => setHoveredOptionLabel(null)}
                        onClick={(e) => {
                            e.stopPropagation();
                            selectOption();
                        }}
                    >
                        {hasSubOptions ? (
                            <div className={styles.SubOptionsContainer}>
                                <div className={styles.SubOptions}>
                                    {visibleSubOptions.map((subOption) => {
                                        const selectSubOption = () => {
                                            onClose();
                                            subOption.action();
                                        };

                                        return (
                                            <div
                                                {...menuItemProps(selectSubOption)}
                                                key={subOption.label}
                                                className={styles.SubOption}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    selectSubOption();
                                                }}
                                            >
                                                {subOption.label}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <>
                                {renderOptionIcon(option, hoveredOptionLabel === option.label)}
                                <span>{option.label}</span>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default ProfileDropdownSheet;
